// src/hooks/useApiCall.js
import { useState } from 'react';
import { handleApiError } from '../components/apiErrorHandler';

/**
 * Hook for button-level loading state on a single async API call.
 * The global loading bar already covers all axios requests automatically —
 * use this only when you specifically need the button itself to show a spinner.
 *
 * Usage:
 *   const { loading, call } = useApiCall();
 *
 *   <Button
 *       loading={loading}
 *       disabled={loading}
 *       onClick={() => call(
 *           () => axiosInstance.post('/api/some-endpoint/'),
 *           (res) => handleApiSuccess('Done.')
 *       )}
 *   />
 */
const useApiCall = () => {
    const [loading, setLoading] = useState(false);

    const call = async (fn, onSuccess) => {
        if (loading) return;   // prevent double-click
        setLoading(true);
        try {
            const result = await fn();
            onSuccess?.(result);
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    return { loading, call };
};

export default useApiCall;
